import { ArgumentError } from "@baseline/common/errors";
import { Empty, MemberOf, Primitive, PrimitiveRef, TypeFor } from "@baseline/common/types";


export class RoutePath<T extends RoutePathArguments = RoutePathArguments>
{
    constructor(path: string, segments: RouteSegment[]);
    constructor(path: string, segments: RouteSegment[], args: Record<string, RoutePathArgumentInfo>);
    constructor(
        readonly path: string,
        readonly segments: RouteSegment[],
        args?: Record<string, RoutePathArgumentInfo>,
    )
    {
        if (args)
            this.arguments = args;
    }

    make(...args: T extends Empty ? [args?: Empty] : [args: T]): string;
    make(args?: any): string
    {
        const path =
            "/" +
            this.segments.map(
                segment =>
                    (typeof segment == "string")
                        ? segment
                        : args[segment.name]?.toString() ?? ""
            ).join('/');
        return path;
    }

    match(path: string): [T, string[]];
    match(segments: string[]): [T, string[]];
    match(arg: string | string[]): [T, string[]]
    {
        // Get segments
        const pathSegments =
            (typeof arg == "string")
                ? arg.split('/').filter(segment => !!segment)
                : arg;

        // Verify segment levels
        if (pathSegments.length < this.segments.length)
            return null;

        // Verify every segments
        const args: RoutePathArguments = {};
        const segments: string[] = [];
        for (let i = 0; i < this.segments.length; i++)
        {
            const segment = this.segments[i];

            // Segment is an argument
            if (typeof segment == "object")
            {
                const { name, type } = segment;
                if (type == Path)
                {
                    const matched = pathSegments.slice(i);
                    args[name] = matched.join('/');
                    segments.push(...matched);
                    break;
                }
                else 
                {
                    args[name] = type(pathSegments[i]);
                    segments.push(pathSegments[i]);
                    continue;
                }
            }

            // Segment is a literal
            if (pathSegments[i] == segment)
                segments.push(pathSegments[i]);
            else
                return null;
        }

        // Return values and segments
        return [args as T, segments];
    }

    arguments: Record<string, RoutePathArgumentInfo>;
}

export namespace RoutePath
{
    export function build(path: string): RoutePath<{}>;
    export function build<T extends RoutePathArgumentTypes>(path: string, argumentTypes: T): RoutePath<RoutePathArgumentsFor<T>>;
    export function build(path: string, argumentTypes?: RoutePathArgumentTypes): RoutePath
    {
        // Split path
        const pathSegments = path.split('/').filter(segment => !!segment);

        // Build segments
        const segments =
            pathSegments
                .map(
                    (segment, index) =>
                    {
                        if (segment.startsWith('{') && segment.endsWith('}'))
                        {
                            const name = segment.slice(1, -1);
                            const type = argumentTypes?.[name];
                            if (!type)
                                throw new ArgumentError(`Argument type was not supplied for '${name}' in '${path}'.`);
                            if (type == Path && index != pathSegments.length - 1)
                                throw new ArgumentError(`Argument '${name}' of type 'Path' must appear last in path '${path}'.`);
                            return ({ name, index, type } as RoutePathArgumentInfo);
                        }
                        return segment;
                    }
                );

        // Argument map
        const pathArgSegments = segments.filter(RoutePathArgumentInfo.isRouteArgumentInfo);
        const pathArgs: Record<string, RoutePathArgumentInfo> =
            (pathArgSegments.length > 0) &&
            Object.fromEntries(
                pathArgSegments.map(argument => [argument.name, argument])
            );

        // Report unknown arguments
        const unknownPathArgs = argumentTypes && Object.keys(argumentTypes).filter(name => !pathArgs[name]);
        if (unknownPathArgs?.length > 0)
            throw new ArgumentError(`Unknown arguments in path '${path}': ${unknownPathArgs.join(", ")}.`);

        // Build and return RoutePath
        const routePath = new RoutePath(path, segments, pathArgs);
        return routePath;
    }
}


export interface RoutePathArgumentInfo
{
    name: string;
    // index: number;
    type: RoutePathArgumentType;
}

export namespace RoutePathArgumentInfo
{
    export function isRouteArgumentInfo(value: unknown): value is RoutePathArgumentInfo;
    export function isRouteArgumentInfo(value: RoutePathArgumentInfo): value is RoutePathArgumentInfo
    {
        return (
            typeof value == "object" &&
            typeof value.name == "string" &&
            // typeof value.index == "number" &&
            !!value.type
        );
    }
}


export type RoutePathArgumentType = PrimitiveRef | Path;


export type RoutePathArgumentTypes = { [K: string]: RoutePathArgumentType; };


export type RoutePathArguments = Record<string, Primitive>;


export type RoutePathArgumentFor<T extends RoutePathArgumentType> = T extends Path ? string : TypeFor<T>;


export type RoutePathArgumentsFor<T extends RoutePathArgumentTypes> = { [K in MemberOf<T>]: RoutePathArgumentFor<T[K]>; };


export type RouteSegment = string | RoutePathArgumentInfo;


export type RoutePathSpecs = string | [string, RoutePathArgumentTypes?];


export type RoutePathFor<T extends RoutePathSpecs> =
    T extends [string, RoutePathArgumentTypes] ? RoutePath<RoutePathArgumentsFor<T[1]>> :
    T extends string ? RoutePath<{}> :
    never;


export type Path = typeof Path;
export const Path = Symbol("Path");

